import {NodeSelection, TextSelection} from 'prosemirror-state';
import {chainCommands, deleteSelection, joinForward, selectNodeForward,} from 'prosemirror-commands';
import isEmpty from 'lodash/isEmpty';

import 'fomantic-ui-less/definitions/behaviors/form';
import 'fomantic-ui-less/definitions/behaviors/api';

import {Sortable} from 'sortablejs';
import {v4 as uuid} from 'uuid';
import {flatten} from 'prosemirror-utils';
import {decode, encode} from 'html-entities';
import {parseJSON, stringifyJSON} from '../../Common/json';
import isEqual from 'lodash/isEqual';
import jsonata from 'jsonata';
import {urlData} from '../../Common/urlData';
import {SWF_FORM_PREFIX} from '../../Common/constants';
import {filterFields} from '../../Common/formFields';
import {api} from "../../Common/fetchWithTimeout";
import view from "pako/lib/zlib/zstream";

const isValidJsonata = value => {
    if (!isEmpty(value)) {
        try {
            jsonata(value);
        } catch (e) {
            return false;
        }
    }

    return true;
};

const jsonataPrompt = (value, promptCallback) => {
    if (!isEmpty(value)) {
        try {
            jsonata(value);
        } catch (e) {
            return promptCallback(e.message);
        }
    }
}

$.fn.form.settings.rules.valueSettings = function (value) {
    const settings = parseJSON(decode(value));
    return !isEmpty(settings?.values) || !isEmpty(settings?.api?.url)
};

$.fn.form.settings.rules.valueSettingsResultSelectorJSONata = function (value) {
    const settings = parseJSON(decode(value));
    if (!isEmpty(settings?.api?.resultSelectorJSONata)) {
        return isValidJsonata(settings.api.resultSelectorJSONata);
    }

    return true;
};

$.fn.form.settings.rules.fieldExists = function (value) {
    return !document.querySelector(`.ui.form.text-editor [name="${SWF_FORM_PREFIX}${value}"]`);
};

$.fn.form.settings.rules.validJsonata = function (value) {
    return isValidJsonata(value);
};

const ruleList = [
    {
        value: 'empty',
        name: 'Is empty',
        defaultPrompt: $.fn.form.settings.prompt.empty,
        hidden: (schema, type) => [schema.nodes.form_choices, schema.nodes.form_checkboxes].includes(type)
    },
    {
        value: 'checked',
        name: 'Is checked',
        defaultPrompt: $.fn.form.settings.prompt.checked,
        hidden: (schema, type) => ![schema.nodes.form_choices, schema.nodes.form_checkboxes].includes(type)
    },
    {
        value: 'email',
        name: 'Is valid email address',
        defaultPrompt: $.fn.form.settings.prompt.email,
        hidden: (schema, type) => schema.nodes.form_input !== type
    },
    {
        value: 'url',
        name: 'Is a valid url',
        defaultPrompt: $.fn.form.settings.prompt.url,
        hidden: (schema, type) => schema.nodes.form_input !== type
    },
    {
        value: 'integer',
        name: 'Is an integer or matches an integer range',
        defaultPrompt: $.fn.form.settings.prompt.integer,
        hidden: (schema, type) => schema.nodes.form_input !== type,
        typeValue: true,
        typeValueRules: {
            optional: true,
            rules: [{
                type: 'regExp[/^\\-?\\d+\\.\\.\\-?\\d+$/]',
                prompt: 'Please enter a valid integer range. Example: 1..10'
            }]
        }
    },
    {
        value: 'decimal',
        name: 'Is a decimal number or matches a decimal range',
        defaultPrompt: $.fn.form.settings.prompt.decimal,
        hidden: (schema, type) => schema.nodes.form_input !== type,
        typeValue: true,
        typeValueRules: {
            optional: true,
            rules: [{
                type: 'regExp[/^\\d+\\.?\\d*\\.\\.\\d+\\.?\\d*$/]',
                prompt: 'Please enter a valid decimal range. Example: 0.1..1.5'
            }]
        }
    },
    {
        value: 'number',
        name: 'Is any number decimal or non-decimal or matches a number range',
        defaultPrompt: $.fn.form.settings.prompt.number,
        hidden: (schema, type) => schema.nodes.form_input !== type,
        typeValue: true,
        typeValueRules: {
            optional: true,
            rules: [{
                type: `regExp[/^\\-?\\d*(\\.\\d+)?\\.\\.\\-?\\d*(\\.\\d+)?$/]`,
                prompt: 'Please enter a valid range. Example: 1..10'
            }]
        }
    },
    {
        value: 'minValue',
        name: 'Has a minimum number decimal or non-decimal',
        defaultPrompt: $.fn.form.settings.prompt.minValue,
        hidden: (schema, type) => schema.nodes.form_input !== type,
        typeValue: true,
        typeValueRules: [
            'empty', 'number'
        ]
    },
    {
        value: 'maxValue',
        name: 'Has a maximum number decimal or non-decimal',
        defaultPrompt: $.fn.form.settings.prompt.maxValue,
        hidden: (schema, type) => schema.nodes.form_input !== type,
        typeValue: true,
        typeValueRules: [
            'empty', 'number'
        ]
    },
    {
        value: 'regExp',
        name: 'Matches against regular expression',
        defaultPrompt: $.fn.form.settings.prompt.regExp,
        hidden: (schema, type) => schema.nodes.form_input !== type,
        typeValue: true,
        typeValueRules: [
            'empty'
        ]
    },
    {
        value: 'creditCard',
        name: 'Is a valid credit card',
        defaultPrompt: $.fn.form.settings.prompt.creditCard,
        hidden: (schema, type) => schema.nodes.form_input !== type
    },
    {
        value: 'contains',
        name: 'Contains text (case insensitive)',
        defaultPrompt: $.fn.form.settings.prompt.contains,
        hidden: (schema, type) => ![schema.nodes.form_input, schema.nodes.form_text_area].includes(type),
        typeValue: true,
        typeValueRules: [
            'empty'
        ]
    },
    {
        value: 'containsExactly',
        name: 'Contains text (case sensitive)',
        defaultPrompt: $.fn.form.settings.prompt.containsExactly,
        hidden: (schema, type) => ![schema.nodes.form_input, schema.nodes.form_text_area].includes(type),
        typeValue: true,
        typeValueRules: [
            'empty'
        ]
    },
    {
        value: 'doesntContain',
        name: 'Doesn\'t contain text (case insensitive)',
        defaultPrompt: $.fn.form.settings.prompt.doesntContain,
        hidden: (schema, type) => ![schema.nodes.form_input, schema.nodes.form_text_area].includes(type),
        typeValue: true,
        typeValueRules: [
            'empty'
        ]
    },
    {
        value: 'doesntContainExactly',
        name: 'Doesn\'t contain text (case sensitive)',
        defaultPrompt: $.fn.form.settings.prompt.doesntContainExactly,
        hidden: (schema, type) => ![schema.nodes.form_input, schema.nodes.form_text_area].includes(type),
        typeValue: true,
        typeValueRules: [
            'empty'
        ]
    },
    {
        value: 'is',
        name: 'Is a value (case insensitive)',
        defaultPrompt: $.fn.form.settings.prompt.is,
        hidden: (schema, type) => schema.nodes.form_input !== type,
        typeValue: true,
        typeValueRules: [
            'empty'
        ]
    },
    {
        value: 'isExactly',
        name: 'Is a value (case sensitive)',
        defaultPrompt: $.fn.form.settings.prompt.isExactly,
        hidden: (schema, type) => ![schema.nodes.form_input, schema.nodes.form_text_area].includes(type),
        typeValue: true,
        typeValueRules: [
            'empty'
        ]
    },
    {
        value: 'not',
        name: 'Is not a value (case insensitive)',
        defaultPrompt: $.fn.form.settings.prompt.not,
        hidden: (schema, type) => ![schema.nodes.form_input, schema.nodes.form_text_area].includes(type),
        typeValueRules: [
            'empty'
        ]
    },
    {
        value: 'notExactly',
        name: 'Is not a value (case sensitive)',
        defaultPrompt: $.fn.form.settings.prompt.notExactly,
        hidden: (schema, type) => ![schema.nodes.form_input, schema.nodes.form_text_area].includes(type),
        typeValue: true,
        typeValueRules: [
            'empty'
        ]
    },
    {
        value: 'minLength',
        name: 'Is greater than a min length',
        defaultPrompt: $.fn.form.settings.prompt.minLength,
        hidden: (schema, type) => ![schema.nodes.form_input, schema.nodes.form_text_area].includes(type),
        typeValue: true,
        typeValueRules: [
            'empty', 'integer', 'integer[0..]'
        ]
    },
    {
        value: 'exactLength',
        name: 'Is exactly length',
        defaultPrompt: $.fn.form.settings.prompt.exactLength,
        hidden: (schema, type) => ![schema.nodes.form_input, schema.nodes.form_text_area].includes(type),
        typeValue: true,
        typeValueRules: [
            'empty', 'integer', 'integer[0..]'
        ]
    },
    {
        value: 'maxLength',
        name: 'Is less than a max length',
        defaultPrompt: $.fn.form.settings.prompt.maxLength,
        hidden: (schema, type) => ![schema.nodes.form_input, schema.nodes.form_text_area].includes(type),
        typeValue: true,
        typeValueRules: [
            'empty', 'integer', 'integer[0..]'
        ]
    },
    {
        value: 'match',
        name: 'Matches the value of another validation field',
        defaultPrompt: $.fn.form.settings.prompt.match,
        typeValue: true,
        typeValueRules: [
            'empty'
        ]
    },
    {
        value: 'different',
        name: 'Be different than another specified field',
        defaultPrompt: $.fn.form.settings.prompt.different,
        typeValue: true,
        typeValueRules: [
            'empty'
        ]
    },
    {
        value: 'minCount',
        name: 'A multiple select field contains at minimum (count) selections',
        defaultPrompt: $.fn.form.settings.prompt.minCount,
        hidden: (schema, type, attrs) => !(schema.nodes.form_checkboxes === type || (schema.nodes.form_select === type && attrs && attrs.multiple)),
        typeValue: true,
        typeValueRules: [
            'empty', 'integer', 'integer[0..]'
        ]
    },
    {
        value: 'exactCount',
        name: 'A multiple select field contains exactly (count) selections',
        defaultPrompt: $.fn.form.settings.prompt.exactCount,
        hidden: (schema, type, attrs) => !(schema.nodes.form_checkboxes === type || (schema.nodes.form_select === type && attrs && attrs.multiple)),
        typeValue: true,
        typeValueRules: [
            'empty', 'integer', 'integer[0..]'
        ]
    },
    {
        value: 'maxCount',
        name: 'A multiple select field contains at maximum (count) selections',
        defaultPrompt: $.fn.form.settings.prompt.maxCount,
        hidden: (schema, type, attrs) => !(schema.nodes.form_checkboxes === type || (schema.nodes.form_select === type && attrs && attrs.multiple)),
        typeValue: true,
        typeValueRules: [
            'empty', 'integer', 'integer[0..]'
        ]
    }
]

export default class FormFieldNodeView {
    constructor(node, view, getPos) {
        this.view = view;
        this.getPos = getPos;
        this.node = node;

        this.dom = this.contentDOM = document.createElement('div');
        this.dom.classList.add('field');
        this.dom.addEventListener("click", () => this.handleClick());

        if (node.attrs.required) {
            this.dom.classList.add('required');
        }

        if (!isEmpty(node.attrs.condition)) {
            this.dom.setAttribute('data-condition', encode(node.attrs.condition));
        }
    }

    handleClick() {
        if (!this.view.editable) {
            return;
        }

        const $pos = this.view.state.doc.resolve(this.getPos());
        const transaction = this.view.state.tr.setSelection(
            new NodeSelection($pos));
        this.view.dispatch(transaction);
    }

    selectNode() {
        if (!this.view.editable) {
            return;
        }

        if (this.editorDom) {
            return;
        }

        const nodePos = this.getPos();
        const schema = this.view.state.schema;
        const {from} = this.view.state.selection;
        const formField = this.view.state.doc.resolve(from).nodeAfter;
        if (!formField) {
            return;
        }

        const formFieldRules = {};

        let labelField, contentField, helpField;
        flatten(formField, false).filter(child => {
            const node = child.node;
            if (!labelField && node.type === schema.nodes.form_label) {
                labelField = node;
            } else if (!helpField && node.type === schema.nodes.form_help) {
                helpField = node;
            } else {
                contentField = node;
            }
        });

        this.dom.classList.add('selected-field');

        this.editorDom = document.createElement('div');
        this.editorDom.setAttribute('contenteditable', 'false');
        this.editorDom.id = `editor-field-${nodePos}`;
        this.editorDom.className = 'ui raised segments transition hidden';

        let fieldProperties = '<div class="two fields">';

        if ([this.view.state.schema.nodes.form_calendar].includes(
            contentField.type)) {
            fieldProperties += `<div class="field required"><label for="name-${nodePos}">Field name</label><input type="text" id="name-${nodePos}" name="name" value="${contentField
                ? contentField?.firstChild?.lastChild?.attrs.name || '' : ''}"></div>`;
        } else {
            fieldProperties += `<div class="field required"><label for="name-${nodePos}">Field name</label><input type="text" id="name-${nodePos}" name="name" value="${contentField
                ? contentField.attrs.name || '' : ''}"></div>`;
        }

        fieldProperties += `<div class="field"><label for="label-${nodePos}">Field label</label><input type="text" id="label-${nodePos}" name="label" value="${labelField
                ? labelField.textContent || '' : ''}"></div>`
            + '</div>';

        if ([this.view.state.schema.nodes.form_select].includes(
            contentField.type)) {

            formFieldRules[`settings-${nodePos}`] = {
                rules: [
                    {
                        type: 'valueSettings',
                        prompt: 'Must have a list of values or API settings for remote data.'
                    },
                    {
                        type: 'valueSettingsResultSelectorJSONata',
                        prompt: value => {
                            const settings = parseJSON(decode(value));
                            return jsonataPrompt(settings?.api?.resultSelectorJSONata, message => `Invalid JSONata expression for <code>api.resultSelectorJSONata</code>. ${message}`);
                        }
                    }
                ]
            };

            fieldProperties += `<div class="field required">
                <label for="settings-${nodePos}">Settings</label>
                <input type="hidden" id="settings-${nodePos}" name="settings" value="${encode(stringifyJSON(contentField.attrs.settings || {}, null, 2))}">
              </div>
              <div id="testRequestValuesField-${nodePos}" class="field"><button id="testRequestValues-${nodePos}" class="ui mini compact button">Test request</button></div>`;
        } else if ([this.view.state.schema.nodes.form_choices, this.view.state.schema.nodes.form_checkboxes].includes(
            contentField.type)) {
            fieldProperties += `<div class="field required"><label for="values-${nodePos}">List of values</label>
                <table id="values-${nodePos}" class="ui striped compact table"><thead>
                <tr>
                <th class="collapsing"></th>
                <th>Label</th>
                <th>Value</th>
                <th class="collapsing"></th>
                </tr>
                </thead><tbody>`;

            contentField.attrs.values.forEach(value => {
                fieldProperties += `<tr class="draggable">
                <td><input type="${(contentField.attrs.multiple
                    || contentField.type
                    === this.view.state.schema.nodes.form_checkboxes) ? 'checkbox'
                    : 'radio'}" name="values-${nodePos}-selected"${value.selected
                    ? ' checked=""' : ''}></td>
                <td><input type="text" name="values-${nodePos}-label" value="${value.name
                || ''}"></td>
                <td><input type="text" name="values-${nodePos}-value" value="${value.value
                || ''}"></td>
                <td class="collapsing selectable"><button class="ui mini compact button">Delete</button></td>
                </tr>`;
            });

            fieldProperties += `</tbody><tfoot>
                <tr>
                <th colspan="4">
                <button id="addDisplayField" class="ui mini compact button">Add item</button>
                </th>
                </tr>
                </tfoot></table>
              </div>`;
        } else if ([this.view.state.schema.nodes.form_calendar].includes(
            contentField.type)) {
            formFieldRules[`calendar-type-${nodePos}`] = {
                rules: [
                    {
                        type: 'regExp[/^datetime|date|time|month|year$/]',
                        prompt: 'Calendar type must be one of: Date and time, Date, Time, Month or Year'
                    }
                ]
            };

            fieldProperties += `<div class="two fields">
                    <div class="field"><label for="value-${nodePos}">Predefined value</label><input type="text" id="value-${nodePos}" name="value" value="${contentField ? contentField.attrs.value || '' : ''}"></div>
                    <div class="field"><label for="placeholder-${nodePos}">Placeholder text</label><input type="text" id="placeholder-${nodePos}" name="placeholder" value="${contentField ? contentField?.firstChild?.lastChild?.attrs.placeholder || '' : ''}"></div>
                </div>
                <div class="two fields">
                    <div class="field required">
                        <label for="calendar-type-${nodePos}">Type</label>
                        <select id="calendar-type-${nodePos}" name="calendar-type" class="ui fluid selection dropdown">
                            <option value="datetime"${(!contentField.attrs.type || contentField.attrs.type === 'datetime') ? ' selected' : ''}>Date and time</option>
                            <option value="date"${(!contentField.attrs.type || contentField.attrs.type === 'date') ? ' selected' : ''}>Date</option>
                            <option value="time"${(!contentField.attrs.type || contentField.attrs.type === 'time') ? ' selected' : ''}>Time</option>
                            <option value="month"${(!contentField.attrs.type || contentField.attrs.type === 'month') ? ' selected' : ''}>Month</option>
                            <option value="year"${(!contentField.attrs.type || contentField.attrs.type === 'year') ? ' selected' : ''}>Year</option>
                        </select>
                    </div>
                </div>`;
        } else {
            fieldProperties += '<div class="two fields">';

            if (contentField.type === this.view.state.schema.nodes.form_text_area) {
                fieldProperties += `<div class="field"><label for="value-${nodePos}">Predefined value</label><textarea id="value-${nodePos}" name="value" rows="2">${contentField ? contentField.textContent : ''}</textarea></div>`;
                fieldProperties += `<div class="field required"><label for="size-${nodePos}">Field rows</label><input type="text" id="size-${nodePos}" name="textarea-rows-${nodePos}" value="${contentField ? contentField.attrs.rows || 10 : 10}"></div>
              </div>`;

                formFieldRules[`size-${nodePos}`] = {
                    rules: [
                        {
                            type: 'integer[1..100]',
                        },
                    ],
                };
            } else {
                fieldProperties += `<div class="field"><label for="value-${nodePos}">Predefined value</label><input type="text" id="value-${nodePos}" name="value" value="${contentField
                    ? contentField.attrs.value || '' : ''}"></div>`;
                fieldProperties += `<div class="field"><label for="placeholder-${nodePos}">Placeholder text</label><input type="text" id="placeholder-${nodePos}" name="placeholder" value="${contentField
                    ? contentField.attrs.placeholder || '' : ''}"></div>`
            }

            fieldProperties += '</div>';
        }

        fieldProperties += `<div class="field"><label for="rules-${nodePos}">Validation rules</label>
                <table id="validationRules" class="ui striped compact table"><thead>
                <tr>
                <th class="eight wide">Rule</th>
                <th class="eight wide">Prompt</th>
                <th class="collapsing"></th>
                </tr>
                </thead><tbody>`;

        formField?.attrs?.rules?.forEach(rule => {
            const id = uuid();
            const matchingRule = ruleList.find(r => r.value === rule.type);

            fieldProperties += `<tr class="draggable">
                <td class="field"><div class="ui search selection dropdown"><input type="hidden" name="rules-${nodePos}-type" value="${rule.type || ''}"><i class="dropdown icon"></i><div class="default text"></div></div>
                ${matchingRule?.typeValue ? `<div class="ui divider"></div><input type="text" id="${id}" name="rules-${nodePos}-type-value" value="${rule.typeValue || ''}">` : ''}
                </td>
                <td class="field"><input type="text" name="rules-${nodePos}-prompt" value="${rule.prompt || ''}" placeholder="${matchingRule?.defaultPrompt}"></td>
                <td class="collapsing selectable"><button class="ui mini compact button">Delete</button></td>
                </tr>`;

            if (matchingRule?.typeValueRules) {
                formFieldRules[id] = matchingRule.typeValueRules;
            }
        });

        fieldProperties += `</tbody><tfoot>
                <tr>
                <th colspan="3">
                <button id="addValidationRule" class="ui mini compact button">Add validation rule</button>
                </th>
                </tr>
                </tfoot></table>
              </div>`;

        formFieldRules[`condition-${nodePos}`] = {
            rules: [
                {
                    type: 'validJsonata',
                    prompt: value => jsonataPrompt(value, message => `Invalid JSONata expression. ${message}`)
                },
            ],
        };

        fieldProperties += `<div class="field">
                <label for="condition-${nodePos}">Conditional logic</label>
                  <textarea id="condition-${nodePos}" name="condition" rows="2">${isEmpty(formField.attrs.condition) ? '' : formField.attrs.condition}</textarea>
                  <div class="ui pointing basic large label">JSONata expression to be evaluated to true to show the field. See <a target="_blank" rel="noopener noreferrer" href="https://jsonata.org/">JSONata</a> for more information about JSONata.</div>
                </div><div class="field">
                <label for="help-${nodePos}">Help text</label>
                  <textarea id="help-${nodePos}" name="help" rows="2">${helpField ? helpField.textContent || '' : ''}</textarea>
                </div>`
            + '<div class="inline fields">'
            + `<div class="field"><div class="ui${formField.attrs.required
                ? ' checked'
                : ''} checkbox"><input id="required-${nodePos}" type="checkbox"${formField.attrs.required
                ? ' checked=""'
                : ''}><label for="required-${nodePos}">Required field</label></div></div>`
            + `<div class="field"><div class="ui${contentField.attrs.readOnly
                ? ' checked'
                : ''} checkbox"><input id="read-only-${nodePos}" type="checkbox"${contentField.attrs.readOnly
                ? ' checked=""'
                : ''}><label for="read-only-${nodePos}">Read-only</label></div></div>`
            + ([this.view.state.schema.nodes.form_choices,
                this.view.state.schema.nodes.form_checkboxes].includes(
                contentField.type)
                ? `<div class="field"><div class="ui${contentField.attrs.sideBySide
                    ? ' checked'
                    : ''} checkbox"><input id="side-by-side-${nodePos}" type="checkbox"${contentField.attrs.sideBySide
                    ? ' checked=""'
                    : ''}><label for="side-by-side-${nodePos}">Side by side</label></div></div>`
                : '')
            + (contentField.type === this.view.state.schema.nodes.form_select ?
                `<div class="field"><div class="ui${contentField.attrs.clearable
                    ? ' clearable'
                    : ''} checkbox"><input id="clearable-${nodePos}" type="checkbox"${contentField.attrs.clearable
                    ? ' checked=""' : ''}><label for="clearable-${nodePos}">Clearable</label></div></div>
                 <div class="field"><div class="ui${contentField.attrs.searchable
                    ? ' searchable'
                    : ''} checkbox"><input id="searchable-${nodePos}" type="checkbox"${contentField.attrs.searchable
                    ? ' checked=""'
                    : ''}><label for="searchable-${nodePos}">Searchable</label></div></div>
                 <div class="field"><div class="ui checkbox"><input id="allowAdditions-${nodePos}" type="checkbox"${contentField.attrs.allowAdditions
                    ? ' checked=""'
                    : ''}><label for="allowAdditions-${nodePos}">Allow additions</label></div></div>`
                : '')
            + (contentField.type === this.view.state.schema.nodes.form_text_area ?
                `<div class="field"><div class="ui${contentField.attrs.richTextEditor
                    ? ' checked'
                    : ''} checkbox"><input id="rich-text-${nodePos}" type="checkbox"${contentField.attrs.richTextEditor
                    ? ' checked=""'
                    : ''}><label for="rich-text-${nodePos}">Rich text editing</label></div></div>`
                : '')
            + '</div>';

        const editorSegment = document.createElement('div');
        editorSegment.classList.add('ui', 'segment', 'form');
        editorSegment.insertAdjacentHTML('afterbegin', fieldProperties);

        this.editorDom.appendChild(editorSegment);

        editorSegment.querySelector(`#testRequestValues-${nodePos}`)?.addEventListener('click', e => {
            editorSegment.querySelector(`#testRequestResponse-${nodePos}`)?.remove();
            $(e.target).api('destroy');

            const form = $(editorSegment).form({
                inline: true,
                duration: 0,
                fields: {
                    [`settings-${nodePos}`]: {
                        rules: [
                            {
                                type: 'valueSettings',
                                prompt: 'Must have a list of values or API settings for remote data.'
                            },
                            {
                                type: 'valueSettingsResultSelectorJSONata',
                                prompt: value => {
                                    const settings = parseJSON(decode(value));
                                    return jsonataPrompt(settings?.api?.resultSelectorJSONata, message => `Invalid JSONata expression for <code>api.resultSelectorJSONata</code>. ${message}`);
                                }
                            }
                        ]
                    }
                },
                className: {
                    label: 'ui basic red pointing prompt large label',
                },
            });
            form.form('validate form');
            if (!form.form('is valid')) {
                return;
            }

            const settings = parseJSON(decode(form.form('get value', 'settings')));
            if (isEmpty(settings?.api?.url)) {
                e.preventDefault();
                return;
            }

            const {credentials, url, method, headers, data, resultSelectorJSONata} = settings.api || {};
            const isSimWorkflowApi = url?.startsWith('/api/');

            async function fetchOAuth2AuthorizeRequired() {
                if (!isEmpty(credentials)) {
                    return await api(`/api/credentials/${credentials}`, {
                        method: 'GET',
                        headers: {
                            'Accept': 'application/json'
                        }
                    })
                        .then(response => response?.credentials?.type === 'OAUTH2' && response?.authorized !== true)
                        .catch(() => false);
                }

                return false;
            }

            fetchOAuth2AuthorizeRequired().then(oAuth2AuthorizeRequired => {
                if (oAuth2AuthorizeRequired) {
                    e.preventDefault();
                    e.stopPropagation();
                    window.open(`/credentials/${credentials}/authorize`, '_blank').focus();
                    return false;
                }

                $(e.target).api({
                    cache: !isSimWorkflowApi,
                    url: isSimWorkflowApi ? url : '/api/proxy',
                    method: isSimWorkflowApi ? method : 'POST',
                    beforeXHR: function (xhr, settings) {
                        if (isSimWorkflowApi) {
                            settings.data = data;

                            if (!isEmpty(headers)) {
                                Object.keys(headers).forEach(headerName => {
                                    xhr.setRequestHeader(headerName,
                                        headers[headerName]);
                                });
                            }
                        } else {
                            settings.data = stringifyJSON({
                                credentials,
                                url: urlData(url, this),
                                method,
                                headers,
                                body: data,
                                input: filterFields($(editorSegment)?.form('get values'))
                            });
                        }
                        xhr.setRequestHeader('Content-Type', 'application/json');
                        xhr.setRequestHeader('Accept', 'application/json');
                        return xhr;
                    },
                    onResponse: function (response) {
                        if (isEmpty(resultSelectorJSONata)) {
                            return response;
                        } else {
                            return jsonata(resultSelectorJSONata).evaluate(response);
                        }
                    },
                    onComplete: function (response, element, xhr) {
                        const {status, statusText} = xhr;
                        document.getElementById(`testRequestResponse-${nodePos}`)?.remove();

                        const jsonResponseField = document.createElement('div');
                        jsonResponseField.id = `testRequestResponse-${nodePos}`;
                        jsonResponseField.className = 'field';
                        if (status === 0 || (status >= 200 && status < 400)) {
                            jsonResponseField.insertAdjacentHTML('beforeend', `<label>HTTP Response: ${xhr.status} ${xhr.statusText}</label>`);
                        } else {
                            jsonResponseField.insertAdjacentHTML('beforeend', `<label><span class="ui error text">HTTP Response: ${xhr.status} ${xhr.statusText}</span></label>`);
                        }

                        import(/* webpackChunkName: "code-editor" */ '../../Common/codeEditor').then(
                            module => module.codeEditor({
                                callbackFn: editor => {
                                    if (!isEmpty(response)) {
                                        editor.setValue(stringifyJSON(response, '', 2));
                                    } else {
                                        editor.setValue('');
                                    }

                                    jsonResponseField.appendChild(editor.getWrapperElement());

                                    editorSegment.querySelector(`#testRequestValuesField-${nodePos}`)?.insertAdjacentElement('afterend', jsonResponseField);

                                    editor.refresh();
                                },
                                errorFn: () => {
                                    jsonResponseField.insertAdjacentHTML('afterend', `<pre class="ui container scrolling very short"><code>${stringifyJSON(response, '', 2)}</code></pre>`);
                                    editorSegment.querySelector(`#testRequestValuesField-${nodePos}`)?.insertAdjacentElement('beforebegin', jsonResponseField);
                                },
                                options: {
                                    mode: 'application/json',
                                    readOnly: true,
                                    foldGutter: true,
                                    lineWrapping: true,
                                    gutters: ['CodeMirror-lint-markers', 'CodeMirror-foldgutter'],
                                },
                            }),
                        );
                    }
                }).api('query');
            });
        });

        const okButton = document.createElement('button');
        okButton.id = `ok-${nodePos}`;
        okButton.type = 'submit';
        okButton.className = 'ui tiny compact button primary';
        okButton.textContent = 'Save';

        const cancelButton = document.createElement('button');
        cancelButton.id = `cancel-${this.dom.id}`;
        cancelButton.className = 'ui tiny compact button';
        cancelButton.textContent = 'Cancel';

        const removeButton = document.createElement('button');
        removeButton.id = `remove-${this.dom.id}`;
        removeButton.className = 'ui tiny compact button';
        removeButton.textContent = 'Remove';

        const buttonsGroup = document.createElement('div');
        buttonsGroup.className = 'ui wrapping spaced buttons';
        buttonsGroup.appendChild(okButton);
        buttonsGroup.appendChild(document.createTextNode('\u00A0'));
        buttonsGroup.appendChild(cancelButton);
        buttonsGroup.appendChild(removeButton);

        const buttonsSegment = document.createElement('div');
        buttonsSegment.className = 'ui secondary segment';
        buttonsSegment.appendChild(buttonsGroup)

        this.editorDom.appendChild(buttonsSegment);
        this.view.dom.parentNode.appendChild(this.editorDom);

        const form = $(editorSegment).form({
            inline: true,
            duration: 0,
            fields: {
                ...formFieldRules
            },
            className: {
                label: 'ui basic red pointing prompt large label',
            },
        });

        if ([this.view.state.schema.nodes.form_select].includes(contentField.type)) {
            import(/* webpackChunkName: "code-editor" */ '../../Common/codeEditor')
                .then(module => module.codeEditor({
                    callbackFn: editor => {
                        editor.setOption('mode', 'application/json');
                        editor.setOption("lint", true);
                        if (!isEmpty(document.getElementById(`settings-${nodePos}`).value)) {
                            editor.setValue(decode(document.getElementById(`settings-${nodePos}`).value));
                        }
                        editor.on('blur', codeMirror => {
                            const inputParametersEl = document.getElementById(`settings-${nodePos}`);
                            const newValue = parseJSON(codeMirror.getValue());
                            if (!isEqual(parseJSON(inputParametersEl.value), newValue)) {
                                inputParametersEl.value = encode(stringifyJSON(newValue));
                            }
                        });

                        const fieldElement = editor.getWrapperElement();
                        fieldElement.classList.add('ui', 'segment', 'basic', 'fitted');

                        document.getElementById(`settings-${nodePos}`).insertAdjacentElement('afterend', fieldElement);

                        editor.refresh();
                    },
                    options: {
                        mode: 'application/json',
                        lint: true,
                    }
                }));
        } else if ([this.view.state.schema.nodes.form_choices,
            this.view.state.schema.nodes.form_checkboxes].includes(
            contentField.type)) {
            const deleteValueRow = e => {
                e.preventDefault();
                if (editorSegment.querySelectorAll('tbody tr').length > 1) {
                    e.target.closest('tr').remove();
                }
            }
            editorSegment.querySelectorAll(`#values-${nodePos} tr td button`).forEach(
                button => button.addEventListener('click', deleteValueRow));

            editorSegment.querySelector('#addDisplayField')?.addEventListener('click',
                e => {
                    e.preventDefault();
                    editorSegment.querySelector(
                        `#values-${nodePos} tbody`)?.insertAdjacentHTML('beforeend', `<tr class="draggable">
                <td><input type="${(contentField.attrs.multiple
                        || contentField.type
                        === this.view.state.schema.nodes.form_checkboxes) ? 'checkbox'
                        : 'radio'}" name="values-${nodePos}-selected"></td>
                <td><input type="text" name="values-${nodePos}-label"></td>
                <td><input type="text" name="values-${nodePos}-value"></td>
                <td class="collapsing selectable"><button class="ui mini compact button">Delete</button></td>
                </tr>`);
                    editorSegment.querySelectorAll(
                        `#values-${nodePos} tr td button`).forEach(
                        button => button.addEventListener('click', deleteValueRow));
                });

            new Sortable(editorSegment.querySelector(`#values-${nodePos} tbody`), {
                draggable: '.draggable',
                chosenClass: 'chosen',
                ghostClass: 'ghost',
                filter: 'input',
                preventOnFilter: false,
                fallbackOnBody: true,
            });
        }

        const deleteRuleRow = e => {
            e.preventDefault();
            e.target.closest('tr').remove();
        }

        const ruleChange = function (value) {
            const matchingRule = ruleList.find(rule => rule.value === value);

            this.closest('tr').querySelector(`input[name="rules-${nodePos}-prompt"]`).placeholder = matchingRule.defaultPrompt;
            const td = this.closest('td');
            const divider = td.querySelector('div[class~="divider"]');
            const typeValue = td.querySelector(`input[name="rules-${nodePos}-type-value"]`);

            if (matchingRule && matchingRule.typeValue) {
                if (!typeValue) {
                    const id = uuid();
                    td.insertAdjacentHTML('beforeend', `<div class="ui divider"></div><input id="${id}" type="text" name="rules-${nodePos}-type-value">`);
                    if (matchingRule.typeValueRules) {
                        form.form('add rule', id, matchingRule.typeValueRules);
                        form.form('set optional', id, matchingRule.typeValueRules.optional || false);
                    }
                } else {
                    form.form('remove rule', typeValue.id);
                    if (matchingRule.typeValueRules) {
                        form.form('add rule', typeValue.id, matchingRule.typeValueRules);
                        form.form('set optional', typeValue.id, matchingRule.typeValueRules.optional || false);
                    }
                }
            } else {
                if (typeValue) {
                    form.form('remove rule', typeValue.id);
                }
                divider && divider.remove();
                typeValue && typeValue.remove();
            }
        }

        editorSegment.querySelectorAll('#validationRules tr td button').forEach(
            button => button.addEventListener('click', deleteRuleRow));

        editorSegment.querySelector('#addValidationRule')?.addEventListener('click',
            e => {
                e.preventDefault();
                let field = `<tr class="draggable">
                <td class="field"><div class="ui search selection dropdown"><input type="hidden" name="rules-${nodePos}-type"><i class="dropdown icon"></i><div class="default text"></div></div></td>
                <td class="field"><input type="text" name="rules-${nodePos}-prompt"></td>
                <td class="collapsing selectable"><button class="ui mini compact button">Delete</button></td>
                </tr>`;
                editorSegment.querySelector(
                    '#validationRules tbody')?.insertAdjacentHTML('beforeend', field);
                editorSegment.querySelectorAll(
                    '#validationRules tr td button').forEach(
                    button => button.addEventListener('click', deleteRuleRow));
                $(editorSegment.querySelector('#validationRules tr:last-of-type .ui.dropdown'))?.dropdown({
                    values: ruleList.filter(rule => !rule.hidden || !rule.hidden(schema, contentField.type, contentField.attrs)),
                    placeholder: 'Select a validation rule',
                    onChange: ruleChange,
                    duration: 0
                });
            });

        new Sortable(editorSegment.querySelector('#validationRules tbody'), {
            draggable: '.draggable',
            chosenClass: 'chosen',
            ghostClass: 'ghost',
            filter: 'input',
            preventOnFilter: false,
            fallbackOnBody: true,
        });

        const validateAndSave = () => {
            form.form('remove field', 'name');
            let nameRules = [
                {
                    type: 'empty'
                },
                {
                    type: 'regExp[/^(swf:|[a-zA-Z])[0-9a-zA-Z_]*$/]',
                    prompt: '{name} can contain ASCII letters (A–Z, a–z), digits (0–9), and underscores, and must begin with a swf: or letter.'
                },
            ];
            if (![contentField.attrs.name, contentField?.firstChild?.lastChild?.attrs.name].includes(document.getElementById(`name-${nodePos}`).value)) {
                nameRules.push({
                    type: 'fieldExists',
                    prompt: 'Field {value} already exists.'
                });
            }
            form.form('add field', 'name', {
                rules: nameRules
            });

            form.form('validate form');
            if (!form.form('is valid')) {
                return;
            }

            const state = this.view.state;

            const label = document.getElementById(`label-${nodePos}`).value;
            const name = document.getElementById(`name-${nodePos}`).value;
            const value = document.getElementById(`value-${nodePos}`)?.value;
            const placeholder = document.getElementById(
                `placeholder-${nodePos}`)?.value;
            const size = document.getElementById(`size-${nodePos}`)?.value;
            const sideBySide = document.getElementById(
                `side-by-side-${nodePos}`)?.checked;
            const clearable = document.getElementById(
                `clearable-${nodePos}`)?.checked;
            const searchable = document.getElementById(
                `searchable-${nodePos}`)?.checked;
            const allowAdditions = document.getElementById(
                `allowAdditions-${nodePos}`)?.checked;
            const richTextEditor = document.getElementById(
                `rich-text-${nodePos}`)?.checked;
            const condition = document.getElementById(`condition-${nodePos}`)?.value;
            const help = document.getElementById(`help-${nodePos}`)?.value;
            const required = document.getElementById(
                `required-${nodePos}`).checked;
            const readOnly = name.startsWith('swf:') || document.getElementById(
                `read-only-${nodePos}`).checked;
            const calendarType = document.getElementById(`calendar-type-${nodePos}`)?.value;

            const field = {};
            const id = name || uuid();

            if (!isEmpty(label)) {
                field.label = state.schema.nodes.form_label.create({id}, state.schema.text(label));
            }

            if (!isEmpty(condition)) {
                field.condition = condition;
            }

            if (!isEmpty(help)) {
                field.help = state.schema.nodes.form_help.create({}, state.schema.text(help));
            }

            if (required) {
                field.required = true;
            }

            const content = {id};
            if (!isEmpty(name)) {
                content.name = name;
            }
            if (readOnly) {
                content.readOnly = true;
            }

            const values = [];
            editorSegment.querySelectorAll(`#values-${nodePos} tbody tr`).forEach(row => {
                const inputs = row.querySelectorAll('input');
                values.push({
                    name: inputs[1].value,
                    value: inputs[2].value,
                    selected: inputs[0].checked ? true : null
                })
            });

            const rules = [];
            editorSegment.querySelectorAll('#validationRules tbody tr').forEach(
                row => {
                    const typeValue = row.querySelector(`input[name="rules-${nodePos}-type-value"]`);

                    rules.push({
                        type: row.querySelector(
                            `input[name="rules-${nodePos}-type"]`).value,
                        typeValue: typeValue ? typeValue.value : null,
                        prompt: row.querySelector(
                            `input[name="rules-${nodePos}-prompt"]`).value
                    })
                });

            if (contentField.type === state.schema.nodes.form_select) {
                content.multiple = contentField.attrs.multiple;
                content.clearable = clearable;
                content.searchable = searchable;
                content.allowAdditions = allowAdditions;
                content.settings = parseJSON(decode(form.form('get value', 'settings')))
                field.content = state.schema.nodes.form_select.create(content);
            } else if (contentField.type === state.schema.nodes.form_choices) {
                content.values = values;
                content.sideBySide = sideBySide;
                field.content = state.schema.nodes.form_choices.create(content);
            } else if (contentField.type === state.schema.nodes.form_checkboxes) {
                content.values = values;
                content.sideBySide = sideBySide;
                field.content = state.schema.nodes.form_checkboxes.create(content);
            } else if (contentField.type === state.schema.nodes.form_text_area) {
                if (!isEmpty(size)) {
                    content.rows = size;
                }
                content.richTextEditor = richTextEditor;

                if (!isEmpty(value)) {
                    field.content = state.schema.nodes.form_text_area.create(content, state.schema.text(value));
                } else {
                    field.content = state.schema.nodes.form_text_area.create(content);
                }
            } else if (contentField.type === state.schema.nodes.form_calendar) {
                field.content = state.schema.nodes.form_calendar.create({
                        type: calendarType,
                        value,
                        readOnly
                    },
                    state.schema.nodes.form_calendar_input.create({},
                        [
                            state.schema.nodes.form_calendar_input_icon.create(),
                            state.schema.nodes.form_calendar_input_field.create({
                                id,
                                name,
                                placeholder
                            }),
                        ]));
            } else {
                if (!isEmpty(placeholder)) {
                    content.placeholder = placeholder;
                }
                if (!isEmpty(value)) {
                    content.value = value;
                }

                content.type = contentField.attrs.type === "password" ? "password" : "text";

                field.content = state.schema.nodes.form_input.create(content);
            }

            const fields = [];
            if (field.label) {
                fields.push(field.label);
            }
            fields.push(field.content);
            if (field.help) {
                fields.push(field.help);
            }

            const offset = state.tr.selection.anchor + 1;
            const tr = state.tr.replaceSelectionWith(
                state.schema.nodes.form_field.createAndFill({
                    required: field.required || false,
                    condition: field.condition || null,
                    rules
                }, fields)).scrollIntoView();
            const resolvedPos = tr.doc.resolve(offset);
            tr.setSelection(TextSelection.near(resolvedPos));
            this.view.dispatch(tr);

            this.deselectNode();
            this.view.focus();
        };

        okButton.addEventListener("click", validateAndSave);
        cancelButton.addEventListener("click", () => {
            this.deselectNode();
            this.view.focus();
        });
        removeButton.addEventListener("click", () => {
            chainCommands(deleteSelection, joinForward, selectNodeForward)(
                this.view.state, this.view.dispatch);
            this.deselectNode();
            this.view.focus();
        });

        const {bottom: formFieldBottom} = this.contentDOM.getBoundingClientRect();
        const {bottom: boxBottom} = this.view.dom.getBoundingClientRect();

        this.editorDom.style.top = 16 + formFieldBottom - boxBottom + "px";

        $(editorSegment.querySelectorAll('.ui.dropdown')).dropdown({
            duration: 0
        });
        editorSegment.querySelectorAll('#validationRules .ui.dropdown').forEach(
            (dropdown, index) => {
                $(dropdown).dropdown({
                    values: ruleList.filter(rule => !rule.hidden || !rule.hidden(schema, contentField.type, contentField.attrs)).map(rule => ({
                        ...rule,
                        selected: formField.attrs.rules[index].type === rule.value
                    })),
                    placeholder: 'Select a validation rule',
                    onChange: ruleChange,
                    duration: 0
                });
            });
        $(this.editorDom).transition({
            animation: 'scale',
            duration: 0
        });
    }

    deselectNode() {
        if (!this.view.editable) {
            return;
        }

        if (this.contentDOM) {
            this.contentDOM.classList.remove('selected-field');
        }

        if (this.editorDom) {
            this.editorDom.remove();
            delete this.editorDom;
        }
    }

    update(node) {
        return this.view.editable;
    }

    stopEvent(e) {
        if (this.view.editable && this.editorDom && /drag/.test(e.type)) {
            this.editorDom.remove();
            delete this.editorDom;
        }
    }

    destroy() {
        this.dom.remove();
        this.contentDOM.remove();
        delete this.dom;
        delete this.contentDOM;
        if (this.editorDom) {
            this.editorDom.remove();
            delete this.editorDom;
        }
    }
}